import { FC, MouseEvent, useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, Popover, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DifferenceIcon from '@mui/icons-material/Difference';
import ReplayIcon from '@mui/icons-material/Replay';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import { comparisonMetricsState } from './comparisonMetrics.state.ts';
import { MAX_COMPARISON_DATES, TComparisonFailedDates } from './comparison.types.ts';

export const ComparisonControl: FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [failedDates, setFailedDates] = useState<TComparisonFailedDates>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<Date | null>(null);

  useEffect(() => {
    const sub = comparisonMetricsState.sub.state().subscribe((s) => {
      setVisible(s.visible);
      setDates(s.dates);
      setFailedDates(s.failedDates);
    });
    return () => sub.unsubscribe();
  }, []);

  const openAddCalendar = (e: MouseEvent<HTMLElement>) => {
    setEditingDate(null);
    setDraftValue(dates.length ? parseISO(dates[dates.length - 1]) : null);
    setAnchorEl(e.currentTarget);
  };
  const openChangeCalendar = (e: MouseEvent<HTMLElement>, date: string) => {
    setEditingDate(date);
    setDraftValue(parseISO(date));
    setAnchorEl(e.currentTarget);
  };
  const closeCalendar = () => {
    setAnchorEl(null);
    setEditingDate(null);
  };

  return (
    <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
      <Divider orientation='vertical' flexItem sx={{ my: 1 }} />
      <Button
        color='primary'
        startIcon={<DifferenceIcon />}
        onClick={() => comparisonMetricsState.pub.toggleVisible()}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('order.compare_to')}
      </Button>
      {visible && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            overflowX: 'auto',
            py: 0.5,
          }}
        >
          {dates.map((date) => (
            <Chip
              key={date}
              size='small'
              clickable
              label={localeFormatterHelper.formatDate(date)}
              color={failedDates[date] ? 'warning' : 'default'}
              title={t('order.comparison_change_date')}
              onClick={(e) => openChangeCalendar(e, date)}
              onDelete={() => comparisonMetricsState.pub.removeDate(date)}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      )}
      {visible && Object.keys(failedDates).length > 0 && (
        <IconButton
          color='warning'
          size='small'
          title={t('order.comparison_retry')}
          aria-label={t('order.comparison_retry')}
          onClick={() => comparisonMetricsState.pub.retryFailedDates()}
          sx={{ flexShrink: 0 }}
        >
          <ReplayIcon />
        </IconButton>
      )}
      {visible && dates.length < MAX_COMPARISON_DATES && (
        <IconButton
          color='primary'
          size='small'
          title={t('order.comparison_add_date')}
          aria-label={t('order.comparison_add_date')}
          onClick={openAddCalendar}
          sx={{ flexShrink: 0 }}
        >
          <AddIcon />
        </IconButton>
      )}
      <Popover
        open={visible && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <DateCalendar
          value={draftValue}
          disableFuture
          onChange={(date, selectionState) => {
            setDraftValue(date);
            if (selectionState !== 'finish' || !date) return;
            const formatted = format(date, 'yyyy-MM-dd');
            if (editingDate) {
              comparisonMetricsState.pub.changeDate(editingDate, formatted);
            } else {
              comparisonMetricsState.pub.addDate(formatted);
            }
            closeCalendar();
          }}
        />
      </Popover>
    </Stack>
  );
};
